import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { customerMeApi } from "@/lib/commerce";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const ENQ_STATUS_LABEL = {
  pending: "Enquiry Pending",
  handled: "Enquiry Handled",
};

export default function MyEnquiries() {
  const { user, loading: authLoading } = useCustomerAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    customerMeApi.inquiries()
      .then((d) => setRows(d.items || []))
      .catch((e) => setErr(e.response?.data?.error?.message || "Could not load your enquiries."));
  }, [user, authLoading]);

  if (authLoading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!user) {
    return (
      <div className="p-10 space-y-3">
        <div className="text-sm">Sign in to view your enquiries.</div>
        <Button asChild><Link to="/customer/login">Sign in</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-enquiries-root">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">FAST FORCE</Link>
          <div className="flex gap-4 text-sm">
            <Link to="/my/orders" className="text-muted-foreground hover:text-foreground">My orders</Link>
            <Link to="/my/enquiries" className="font-medium underline underline-offset-4">My enquiries</Link>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-4">
        <h1 className="text-3xl font-semibold">My enquiries</h1>
        {err && <div className="text-sm text-destructive">{err}</div>}
        {!rows ? (
          <div className="text-sm text-muted-foreground">Loading your enquiries…</div>
        ) : rows.length === 0 ? (
          <div className="border rounded-lg p-12 text-center text-sm text-muted-foreground" data-testid="my-enquiries-empty">
            You haven&rsquo;t made any enquiries yet.
            <div className="mt-3"><Button asChild><Link to="/catalog">Browse parts</Link></Button></div>
          </div>
        ) : (
          rows.map((e) => (
            <Card key={e.id} data-testid={`my-enquiry-${e.inquiry_number}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Enquiry</div>
                  <CardTitle className="font-mono text-base">{e.inquiry_number}</CardTitle>
                </div>
                {e.converted_to_order_number ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">
                    Converted → {e.converted_to_order_number}
                  </Badge>
                ) : (
                  <Badge variant={e.status === "handled" ? "secondary" : "outline"}>
                    {ENQ_STATUS_LABEL[e.status] || "Enquiry Pending"}
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="font-medium">{e.product_name || "—"}</div>
                {e.product_sku && (
                  <div className="text-xs text-muted-foreground font-mono">{e.product_sku}</div>
                )}
                {e.note && (
                  <div className="text-xs text-muted-foreground mt-1">Note: {e.note}</div>
                )}
                <div className="text-xs text-muted-foreground pt-2">
                  Sent {new Date(e.created_at).toLocaleString()}
                </div>
                {e.converted_to_order_number && (
                  <Button asChild size="sm" variant="outline" className="mt-2">
                    <Link to={`/track?order_number=${e.converted_to_order_number}`}>
                      Track order →
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </main>
    </div>
  );
}
